/**
 * Browse an Azure Blob connection. Rendered inside `ConnectionBrowserPage`,
 * which owns the page header and the connection picker
 * (FS-909). List containers → list files → download; upload a file;
 * admin-only create/delete container.
 *
 * Credentials resolve server-side. Download streams bytes; upload is
 * multipart; container ops + upload are audited server-side.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  FileInput,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconFileZip, IconRefresh, IconTrash, IconUpload } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useMe } from "@/features/users/useMe";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  blobCreateContainer,
  blobDeleteContainer,
  blobDeleteFile,
  blobDownloadAll,
  blobFileContentUrl,
  blobListContainers,
  blobListFiles,
  blobUploadFile,
  downloadFile,
  type BlobSummary,
  type ContainerSummary,
} from "@/shared/api/integration-browse";

export function BlobBrowser({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const isAdmin = me?.roles.includes("administrator") ?? false;

  const [containers, setContainers] = useState<ContainerSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  // Current folder within the container ("" = root). The blob list is flat, so
  // we scope it with `prefix` and derive subfolders client-side by splitting the
  // remaining path on "/".
  const [prefix, setPrefix] = useState("");
  const [files, setFiles] = useState<BlobSummary[]>([]);
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newContainer, setNewContainer] = useState("");
  const [uploadCandidates, setUploadCandidates] = useState<File[]>([]);
  // "3 of 7" while a batch runs, so a slow upload doesn't look stalled.
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadContainers = useCallback(async () => {
    setLoadingContainers(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await blobListContainers(token, connectionId);
      setContainers(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingContainers(false);
    }
  }, [getToken, connectionId]);

  const loadFiles = useCallback(
    async (container: string, atPrefix: string) => {
      setLoadingFiles(true);
      try {
        const token = await getToken();
        const result = await blobListFiles(token, connectionId, container, atPrefix || null);
        setFiles(result.items);
      } catch (err) {
        toastApiError("Could not list files", err);
        setFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    },
    [getToken, connectionId],
  );

  // Re-scan the store now: reload containers, and the current folder's files.
  const onRefresh = useCallback(async () => {
    await loadContainers();
    if (selected) await loadFiles(selected, prefix);
  }, [loadContainers, loadFiles, selected, prefix]);

  useEffect(() => {
    void loadContainers();
  }, [loadContainers]);

  useEffect(() => {
    if (selected) void loadFiles(selected, prefix);
    else setFiles([]);
  }, [selected, prefix, loadFiles]);

  // Immediate subfolders + files at the current prefix, derived from the flat
  // listing (names are full blob paths). A name with a "/" after the prefix is a
  // subfolder; otherwise it's a file in this folder.
  const subfolders = new Set<string>();
  const filesHere: BlobSummary[] = [];
  for (const f of files) {
    const rest = f.name.startsWith(prefix) ? f.name.slice(prefix.length) : f.name;
    const slash = rest.indexOf("/");
    if (slash > 0) subfolders.add(rest.slice(0, slash));
    else if (rest) filesHere.push(f);
  }
  const folderNames = [...subfolders].sort();
  const crumbs = prefix ? prefix.replace(/\/$/, "").split("/") : [];

  const onCreateContainer = async () => {
    const name = newContainer.trim();
    if (!name) return;
    const reason = await promptForReason(`Create container "${name}"`, "Audit reason", {
      confirmLabel: "Create",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await blobCreateContainer(token, connectionId, name, reason);
      toastSuccess("Container created", name);
      setNewContainer("");
      await loadContainers();
      setSelected(name);
    } catch (err) {
      toastApiError("Create failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteContainer = async (name: string) => {
    const reason = await promptForReason(`Delete container "${name}"`, "Audit reason", {
      warning: "This permanently deletes the container and its contents. This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await blobDeleteContainer(token, connectionId, name, reason);
      toastWarning("Container deleted", name);
      if (selected === name) setSelected(null);
      await loadContainers();
    } catch (err) {
      toastApiError("Delete failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDeleteFile = async (blob: BlobSummary) => {
    if (!selected) return;
    const shown = blob.name.split("/").pop() ?? blob.name;
    const reason = await promptForReason(`Delete "${shown}"`, "Audit reason", {
      warning: "This permanently deletes the file. This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await blobDeleteFile(token, connectionId, selected, blob.name, reason);
      toastWarning("File deleted", shown);
      await loadFiles(selected, prefix);
    } catch (err) {
      toastApiError("Delete failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (blob: BlobSummary) => {
    if (!selected) return;
    try {
      const token = await getToken();
      await downloadFile(
        token,
        blobFileContentUrl(connectionId, selected, blob.name),
        blob.name.split("/").pop() ?? blob.name,
      );
    } catch (err) {
      toastApiError("Download failed", err);
    }
  };

  const onDownloadAll = async () => {
    if (!selected) return;
    setBulkBusy(true);
    try {
      const token = await getToken();
      const res = await blobDownloadAll(token, connectionId, selected, prefix || null);
      toastSuccess(
        "Download ready",
        res.truncated
          ? `Zipped the first ${res.count} of ${res.matched} files (capped).`
          : `Zipped ${res.count} file${res.count === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      toastApiError("Download all failed", err);
    } finally {
      setBulkBusy(false);
    }
  };

  const onUpload = async () => {
    if (!selected || uploadCandidates.length === 0) return;
    setBusy(true);
    // Uploaded one request per file rather than through a bulk endpoint, on
    // purpose: each file gets its own `file.uploaded` audit row naming it, which
    // a single batched event could not provide. Sequential rather than parallel
    // so a large batch can't swamp the storage account or the API.
    const failed: string[] = [];
    let uploaded = 0;
    try {
      const token = await getToken();
      for (const [index, file] of uploadCandidates.entries()) {
        setUploadProgress(`${index + 1} of ${uploadCandidates.length}`);
        try {
          await blobUploadFile(token, connectionId, selected, `${prefix}${file.name}`, file);
          uploaded += 1;
        } catch {
          // One bad file must not abandon the rest — collect and report at the
          // end rather than stopping the batch.
          failed.push(file.name);
        }
      }
      if (uploaded > 0) {
        toastSuccess(
          uploaded === 1 ? "Uploaded" : `Uploaded ${uploaded} files`,
          `${selected}/${prefix}`,
        );
      }
      if (failed.length > 0) {
        // Named, not counted: "3 failed" is not actionable.
        toastApiError(
          `${failed.length} file${failed.length === 1 ? "" : "s"} failed`,
          new Error(failed.join(", ")),
        );
      }
      setUploadCandidates([]);
      await loadFiles(selected, prefix);
    } catch (err) {
      toastApiError("Upload failed", err);
    } finally {
      setUploadProgress(null);
      setBusy(false);
    }
  };

  return (
    <Stack gap="md">
      {/* The page header and connection picker belong to ConnectionBrowserPage;
          Refresh stays here because it acts on this browser's own data. */}
      <Group justify="flex-end">
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={16} />}
          onClick={() => void onRefresh()}
          loading={loadingContainers || loadingFiles}
        >
          Refresh
        </Button>
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load containers">
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Group align="flex-end" gap="sm">
          <Select
            label="Container"
            placeholder={loadingContainers ? "Loading…" : "Select a container"}
            data={containers.map((c) => c.name)}
            value={selected}
            onChange={(v) => {
              setSelected(v);
              setPrefix(""); // start at the container root
            }}
            searchable
            style={{ flex: 1 }}
          />
          {isAdmin && selected && (
            <Button
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />}
              onClick={() => void onDeleteContainer(selected)}
              disabled={busy}
            >
              Delete container
            </Button>
          )}
        </Group>

        {isAdmin && (
          <Group align="flex-end" gap="sm" mt="sm">
            <TextInput
              label="New container"
              placeholder="my-container"
              description="Lowercase letters, numbers and hyphens; 3–63 chars; start/end alphanumeric."
              value={newContainer}
              onChange={(e) => setNewContainer(e.currentTarget.value)}
              error={containerNameError(newContainer)}
              style={{ flex: 1 }}
            />
            <Button
              onClick={() => void onCreateContainer()}
              disabled={busy || containerNameError(newContainer) !== null || !newContainer.trim()}
            >
              Create
            </Button>
          </Group>
        )}
      </Paper>

      {selected && (
        <Paper withBorder radius="md" p="md">
          <Group align="flex-end" gap="sm">
            <FileInput
              multiple
              label="Upload files"
              description={
                uploadProgress
                  ? `Uploading ${uploadProgress}…`
                  : "Pick one or several. Each is uploaded and audited separately."
              }
              placeholder="Choose files…"
              value={uploadCandidates}
              onChange={setUploadCandidates}
              clearable
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => void onUpload()}
              loading={busy && uploadProgress !== null}
              disabled={busy || uploadCandidates.length === 0}
            >
              {uploadCandidates.length > 1 ? `Upload ${uploadCandidates.length}` : "Upload"}
            </Button>
          </Group>
        </Paper>
      )}

      {selected && (
        <Group gap={4} wrap="wrap">
          <Anchor size="sm" onClick={() => setPrefix("")}>
            {selected}
          </Anchor>
          {crumbs.map((seg, i) => (
            <Group key={i} gap={4} wrap="nowrap">
              <Text size="sm" c="dimmed">
                /
              </Text>
              <Anchor size="sm" onClick={() => setPrefix(crumbs.slice(0, i + 1).join("/") + "/")}>
                {seg}
              </Anchor>
            </Group>
          ))}
        </Group>
      )}

      {selected && filesHere.length > 0 && (
        <Group justify="flex-end">
          <Button
            variant="default"
            size="xs"
            leftSection={<IconFileZip size={16} />}
            onClick={() => void onDownloadAll()}
            loading={bulkBusy}
          >
            Download all in this folder
          </Button>
        </Group>
      )}

      <Paper withBorder radius="md" p={0}>
        {!selected ? (
          <Text c="dimmed" p="xl" ta="center">
            Select a container to browse.
          </Text>
        ) : loadingFiles ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : folderNames.length === 0 && filesHere.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            This folder is empty.
          </Text>
        ) : (
          <Table verticalSpacing="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Content type</Table.Th>
                <Table.Th style={{ width: 96 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {folderNames.map((name) => (
                <Table.Tr
                  key={`dir:${name}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setPrefix(`${prefix}${name}/`)}
                >
                  <Table.Td>📁 {name}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      folder
                    </Text>
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              ))}
              {filesHere.map((f) => (
                <Table.Tr key={f.name}>
                  <Table.Td>{f.name.slice(prefix.length)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatBytes(f.size_bytes)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {f.content_type ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap" justify="flex-end">
                      <Tooltip label="Download">
                        <ActionIcon
                          variant="subtle"
                          color="brand"
                          onClick={() => void onDownload(f)}
                          aria-label={`Download ${f.name}`}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={busy}
                          onClick={() => void onDeleteFile(f)}
                          aria-label={`Delete ${f.name}`}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

/** Azure container-naming rules, checked client-side so an invalid name can't
 *  be submitted (it would fail server-side with an opaque error). Returns an
 *  error string for the field, or null when the name is empty or valid. */
function containerNameError(name: string): string | null {
  if (!name) return null;
  if (name.length < 3 || name.length > 63) return "Must be 3–63 characters.";
  if (!/^[a-z0-9-]+$/.test(name)) return "Only lowercase letters, numbers, and hyphens.";
  if (!/^[a-z0-9]/.test(name) || !/[a-z0-9]$/.test(name))
    return "Must start and end with a letter or number.";
  if (name.includes("--")) return "No consecutive hyphens.";
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
